import makeMap from '@qdabra/forms-shared/makeMap';
import { nsXmlns } from '@qdabra/forms-shared/xmlNamespaces';
import { attributeValue } from 'func-xml';
import { curry, filter, prop, toPairs } from 'ramda';
import { XPathEngine } from '../../Manifest/formsviewer.domain';
import fvNamespaceResolver from '../../fvNamespaceResolver';
import {
    getChildNodes,
    getDocumentElement,
    getFirstChildElement,
    isDocumentNode,
    ownerDocument,
    xmlToString,
} from '../xmlUtility';

const $ = typeof window !== 'undefined' && window.jQuery;

const DOM_PROCESSING_INSTRUCTION_NODE = 7;

export const parseXmlNative = (xml) => {
    if (!$) {
        throw new Error('XML parser not available');
    }

    return $.parseXML(xml);
};

export const getFieldHtml = function (node) {
    if (!node.childNodes) {
        return null;
    }

    return $('<div>').append($(node.childNodes).clone()).html();
};

export const xmlStringToBoolean = (value) => value === 'true' || value === '1';

export const hasYesAttribute = curry(
    (attributeName, element) =>
        attributeValue(attributeName)(element) === 'yes',
);

export const makeTextNode = function (refNode, val) {
    var owner = ownerDocument(refNode);

    return owner.createTextNode(val);
};

// Processing instructions

// DomNode, String -> ProcessingInstruction
// Retrieves the child processing instruction with the specified name belonging
// to the specified node, or null if no such processing instruction is found
export const getProcessingInstruction = (node, name) =>
    Array.from(node.childNodes).filter(
        (n) =>
            n.nodeType === DOM_PROCESSING_INSTRUCTION_NODE &&
            n.nodeName === name,
    )[0];

export const addNewPi = function (node, name) {
    const ownerDoc = ownerDocument(node);
    const firstElement = getFirstChildElement(node);
    let pi = ownerDoc && ownerDoc.createProcessingInstruction(name, '');

    if (!pi) {
        return null;
    }

    if (firstElement) {
        node.insertBefore(pi, firstElement);
    } else {
        node.appendChild(pi);
    }

    return pi;
};

// DomNode[ProcessingInstruction] -> DomNode[Element]
// Returns a dom element containing attributes to correspond to the specified
// processing instruction's pseudo-attributes
export const makePiDom = (processingInstruction) =>
    parseXmlNative(`<n ${processingInstruction.nodeValue} />`).childNodes[0];

export const isNamespaceDeclaration = (attr) =>
    attr.namespaceURI === nsXmlns || attr.prefix === 'xmlns';

const getNamespaceNodes = (node) =>
    filter(isNamespaceDeclaration, node.attributes || []);

export const buildNamespaceManagerFromMap = (nsMap) => {
    const manager = new fvNamespaceResolver();

    for (const [prefix, uri] of toPairs(nsMap)) {
        manager.addNamespace(prefix, uri);
    }

    return manager;
};

// DomNode, Constructor[NamespaceResolver] -> NamespaceResolver
// Creates a namespace resolver of the indicated type using the namespace declarations on the indicated node.
export const buildNamespaceManager = (node) => {
    if (isDocumentNode(node)) {
        node = getDocumentElement(node);
    }

    const nsNodes = getNamespaceNodes(node);

    const map = makeMap(
        (mapNode) => mapNode.localName || mapNode.baseName,
        prop('nodeValue'),
        nsNodes,
    );

    return buildNamespaceManagerFromMap(map);
};

export const makeXPathEngine = (namespaceResolver) => {
    let xPathEngine = new XPathEngine();

    xPathEngine.nsr = namespaceResolver;

    return xPathEngine;
};

export const makeXPathEngineFromNamespaces = (nsMap) => {
    const nsr = buildNamespaceManagerFromMap(nsMap);

    return makeXPathEngine(nsr);
};

export const xmlToStringMulti = function (nodes, separator = '') {
    nodes = (nodes && Array.prototype.slice.call(nodes)) || [];

    return nodes.map(xmlToString).join(separator);
};

// Creates a new DOMDocument with a clone of the specified node as its root
export const createDocument = (rootNode) => {
    const doc = parseXmlNative('<n />');

    doc.removeChild(doc.documentElement);
    doc.appendChild(rootNode.cloneNode(true));

    return doc;
};

export const deleteNode = (node) => {
    const parent = node.parentNode;

    if (parent) {
        parent.removeChild(node);

        return;
    }

    const owner = node.ownerElement;

    if (owner) {
        owner.removeAttributeNode(node);

        return;
    }

    throw new Error('Unable to locate parent of node to remove.');
};

// 2024-06-04 - Moved from util.js.
export const copyNodeContent = (srcNode, destNode) => {
    if (!(srcNode && destNode)) {
        return;
    }

    $(destNode).empty();

    var children = getChildNodes(srcNode);

    children.forEach((child) => {
        destNode.appendChild(child.cloneNode(true));
    });
};
